import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Users, MapPin, School, Ruler, Weight } from "lucide-react";

const formatDate = (value) => {
  if (!value) return "No SWOT";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No SWOT";
  return date.toLocaleDateString();
};

const formatLocation = (fullAddress) => {
  if (!fullAddress) return "Location N/A";
  const parts = fullAddress.split(",");
  if (parts.length >= 3) {
    const city = parts[parts.length - 2]?.trim();
    const stateZip = parts[parts.length - 1]?.trim();
    const state = stateZip?.split(" ")[0];
    return `${city}, ${state}`;
  }
  return fullAddress;
};

export default function SkillsTrainerSwotHub() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [athletes, setAthletes] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchPrograms = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: programData, error } = await supabase
          .from("programs")
          .select("*")
          .eq("administrator", user.id);

        if (error) throw error;
        setPrograms(programData || []);

        if (programData?.length) {
          setSelectedProgramId(String(programData[0].id));
        }
      } catch (err) {
        console.error("Error loading programs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    const fetchAthletes = async () => {
      if (!selectedProgramId) return;
      setLoading(true);
      try {
        const { data: rostered, error: rosterError } = await supabase
          .from("user_program_follows")
          .select(`
            user_id,
            users (
              id, full_name, profile_img, gender, grade,
              user_school, home_location, height_feet,
              height_inches, weight, email
            )
          `)
          .eq("program_id", selectedProgramId);

        if (rosterError) throw rosterError;

        const { data: swotRows, error: swotError } = await supabase
          .from("athlete_swot")
          .select("athlete_id, updated_at")
          .eq("program_id", selectedProgramId);

        if (swotError) throw swotError;

        const swotMap = new Map();
        swotRows?.forEach((row) => {
          swotMap.set(String(row.athlete_id), row.updated_at);
        });

        const formatted = (rostered || [])
          .map((row) => row.users)
          .filter(Boolean)
          .map((user) => ({
            id: user.id,
            name: user.full_name || "Unnamed Athlete",
            profile_img: user.profile_img || null,
            gender: user.gender,
            grade: user.grade,
            user_school: user.user_school,
            home_location: user.home_location,
            height_feet: user.height_feet,
            height_inches: user.height_inches,
            weight: user.weight,
            lastUpdated: swotMap.get(String(user.id)) || null,
          }));

        formatted.sort((a, b) => a.name.localeCompare(b.name));
        setAthletes(formatted);
      } catch (err) {
        console.error("Error loading athletes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAthletes();
  }, [selectedProgramId]);

  const filteredAthletes = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return athletes;
    return athletes.filter((athlete) =>
      athlete.name.toLowerCase().includes(term)
    );
  }, [athletes, search]);

  if (loading && programs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="pt-6 px-4 pb-20 min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto text-center bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <ShieldCheck className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Programs Found</h1>
          <p className="text-sm text-gray-600">
            Your account isn’t linked to a program yet. Once a program is assigned, you’ll
            see athletes here for SWOT work.
          </p>
        </div>
      </div>
    );
  }

  const currentSport = programs.find(p => String(p.id) === selectedProgramId)?.sport;

  return (
    <div className="pt-0 pb-24 md:p-6 md:pb-6 min-h-screen text-gray-900 bg-gray-50">
      <div className="bg-white md:rounded-2xl shadow-sm border-b md:border border-gray-200 px-4 py-6 md:p-6 mb-6 mt-0 pt-6 md:pt-6">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">
              SWOT Hub
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage SWOT analysis across your trainer program.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
            <Users className="w-4 h-4" />
            {filteredAthletes.length} Athletes
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 block">
              Program
            </label>
            <select
              className="w-full appearance-none bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl py-3 px-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
            >
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 block">
              Search Athlete
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pl-9 pr-3 text-sm text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-500 animate-pulse">Loading athletes...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 md:px-0">
          {filteredAthletes.map((athlete) => (
            <div
              key={athlete.id}
              className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="w-14 h-14 rounded-full bg-gray-100 border-2 border-white shadow-sm overflow-hidden flex-shrink-0 flex items-center justify-center text-xl font-bold text-gray-400">
                  {athlete.profile_img ? (
                    <img
                      src={athlete.profile_img}
                      alt={athlete.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    (athlete.name || "U").charAt(0)
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 leading-tight truncate">
                        {athlete.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                          Client
                        </span>
                        {athlete.grade && (
                          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">
                            Grade {athlete.grade}
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-lg ${athlete.lastUpdated
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-600"
                        }`}
                    >
                      {formatDate(athlete.lastUpdated)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-y-2 gap-x-1 text-xs text-gray-600 mb-4 bg-gray-50/50 p-3 rounded-xl">
                <div className="flex items-center gap-2">
                  <School size={14} className="text-gray-400" />
                  <span className="truncate" title={athlete.user_school}>
                    {athlete.user_school || "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-gray-400" />
                  <span className="truncate">{formatLocation(athlete.home_location)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Ruler size={14} className="text-gray-400" />
                  <span>
                    {athlete.height_feet ? `${athlete.height_feet}'${athlete.height_inches || 0}"` : "-"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Weight size={14} className="text-gray-400" />
                  <span>{athlete.weight ? `${athlete.weight} lbs` : "-"}</span>
                </div>
              </div>

              <button
                onClick={() => navigate(`/skills-trainer/swot/${athlete.id}`, {
                  state: { sport: currentSport, programId: selectedProgramId }
                })}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-500 transition-colors"
              >
                <span className="text-sm font-semibold text-gray-700">Open SWOT Analysis</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-gray-400"
                  fill="none" viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          ))}
          {filteredAthletes.length === 0 && (
            <div className="col-span-full text-center text-sm text-gray-500 py-10 bg-white border border-dashed border-gray-200 rounded-xl">
              No athletes found with your current filters.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
